import {
  WorkflowStateSelection,
  WorkflowStateSelectionConfigDto,
} from '@asset-metrix/portal-gateway-client-js';
import type { StateCreator } from 'zustand';

import { RemoveFunctions } from '../../utils/types';

export type WorkflowStateSelectionSlice = {
  workflowStateSelections: Partial<{
    [pageFilterInstanceId: string]: Partial<{
      [widgetFilterInstanceId: string]: WorkflowStateSelection[];
    }>;
  }>;
  addWorkflowStateSelection(
    pageFilterInstanceId: string,
    widgetFilterInstanceId: string,
    newSelection: WorkflowStateSelection
  ): void;
  removeWorkflowStateSelection(
    pageFilterInstanceId: string,
    widgetFilterInstanceId: string,
    selectionToRemove: string
  ): void;
  initWorkflowStateSelection(
    pageFilterInstanceId: string,
    widgetFilterInstanceId: string,
    config: WorkflowStateSelectionConfigDto
  ): void;
  cloneWorkflowStateSelection(
    pageFilterInstanceIdFrom: string,
    widgetFilterInstanceIdFrom: string,
    pageFilterInstanceIdTo: string,
    widgetFilterInstanceIdTo: string
  ): void;
  deleteWorkflowStateSelection(
    pageFilterInstanceId: string,
    widgetFilterInstanceId: string
  ): void;
  resetWorkflowStateSelectionSlice(): void;
};

const initialState: RemoveFunctions<WorkflowStateSelectionSlice> = {
  workflowStateSelections: {},
};

export const createWorkflowStateSelectionSlice: StateCreator<
  WorkflowStateSelectionSlice,
  [],
  [],
  WorkflowStateSelectionSlice
> = (set) => {
  return {
    ...initialState,
    addWorkflowStateSelection(
      pageFilterInstanceId,
      widgetFilterInstanceId,
      newSelection
    ) {
      set((state) => ({
        workflowStateSelections: {
          ...state.workflowStateSelections,
          [pageFilterInstanceId]: {
            ...state.workflowStateSelections[pageFilterInstanceId],
            [widgetFilterInstanceId]: [
              ...(state.workflowStateSelections[pageFilterInstanceId]?.[
                widgetFilterInstanceId
              ] ?? []),
              newSelection,
            ],
          },
        },
      }));
    },
    removeWorkflowStateSelection(
      pageFilterInstanceId,
      widgetFilterInstanceId,
      selectionToRemove
    ) {
      set((state) => ({
        workflowStateSelections: {
          ...state.workflowStateSelections,
          [pageFilterInstanceId]: {
            ...state.workflowStateSelections[pageFilterInstanceId],
            [widgetFilterInstanceId]: state.workflowStateSelections[
              pageFilterInstanceId
            ]?.[widgetFilterInstanceId]?.filter(
              ({ label }) => label !== selectionToRemove
            ),
          },
        },
      }));
    },
    initWorkflowStateSelection(
      pageFilterInstanceId,
      widgetFilterInstanceId,
      config
    ) {
      set((state) => {
        if (
          state.workflowStateSelections[pageFilterInstanceId]?.[
            widgetFilterInstanceId
          ]
        ) {
          return state;
        }

        return {
          workflowStateSelections: {
            ...state.workflowStateSelections,
            [pageFilterInstanceId]: {
              ...state.workflowStateSelections[pageFilterInstanceId],
              [widgetFilterInstanceId]: config.options.filter(
                (option) => option.initiallyChecked
              ),
            },
          },
        };
      });
    },
    cloneWorkflowStateSelection(
      pageFilterInstanceIdFrom,
      widgetFilterInstanceIdFrom,
      pageFilterInstanceIdTo,
      widgetFilterInstanceIdTo
    ) {
      set((state) => {
        const selections =
          state.workflowStateSelections[pageFilterInstanceIdFrom]?.[
            widgetFilterInstanceIdFrom
          ];
        if (selections == null) {
          return state;
        }

        return {
          workflowStateSelections: {
            ...state.workflowStateSelections,
            [pageFilterInstanceIdTo]: {
              ...state.workflowStateSelections[pageFilterInstanceIdTo],
              [widgetFilterInstanceIdTo]: [...selections],
            },
          },
        };
      });
    },
    deleteWorkflowStateSelection(pageFilterInstanceId, widgetFilterInstanceId) {
      set((state) => {
        const pageSelections =
          state.workflowStateSelections[pageFilterInstanceId];
        if (pageSelections?.[widgetFilterInstanceId] == null) {
          return state;
        }

        const { [widgetFilterInstanceId]: _, ...remainingWidgetSelections } =
          pageSelections;

        return {
          workflowStateSelections: {
            ...state.workflowStateSelections,
            [pageFilterInstanceId]: remainingWidgetSelections,
          },
        };
      });
    },
    resetWorkflowStateSelectionSlice() {
      set({ ...initialState });
    },
  };
};
